import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';

const ProductionPlanningPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    production_calendar: false,
    batch_requests: false,
    manufacturing_orders: false
  });
  
  // Manufacturing pages mapping
  const manufacturingPages = [
    'production_calendar', 
    'batch_requests'
  ];
  
  const pageNameMapping = {
    'production_calendar': 'Production Planning Page',
    'batch_requests': 'Requests Page'
  };
  
  const pageIconMapping = {
    'production_calendar': <CalendarMonthIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'batch_requests': <AssignmentIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderManufacturingPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'production_calendar') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.calendar'));
    } else if (pageKey === 'batch_requests') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.batch_requests.'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission - only for production_calendar */}
            {viewPerm && pageKey !== 'batch_requests' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: editPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission - only for production_calendar */}
            {editPerm && pageKey !== 'batch_requests' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: deletePerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - only for production_calendar */}
            {deletePerm && pageKey !== 'batch_requests' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Manufacturing Orders section for batch_requests */}
            {pageKey === 'batch_requests' && (
              <Box sx={{ mt: 2 }}>
                <Box
                  onClick={() => setExpandedSections(prev => ({ ...prev, manufacturing_orders: !expandedSections.manufacturing_orders }))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    mb: 1,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      Manufacturing Orders
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {expandedSections.manufacturing_orders ? '▼' : '▶'}
                  </Typography>
                </Box>

                <Collapse in={expandedSections.manufacturing_orders}>
                  <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                    {/* Manufacturing Orders View */}
                    {(() => {
                      const permission = permissions.find(p => p.name === 'production.batch_requests.manufacturing_orders.view');
                      return permission ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          py: 1,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              View
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Can view manufacturing orders
                            </Typography>
                          </Box>
                          <Switch
                            checked={roleForm.permissions[permission.id]?.read || false}
                            onChange={(e) => {
                              const newPermissions = { ...roleForm.permissions };
                              if (!newPermissions[permission.id]) {
                                newPermissions[permission.id] = {};
                              }
                              newPermissions[permission.id].read = e.target.checked;
                              setRoleForm({ ...roleForm, permissions: newPermissions });
                            }}
                            size="small"
                          />
                        </Box>
                      ) : null;
                    })()}

                    {/* Manufacturing Orders Edit */}
                    {(() => {
                      const permission = permissions.find(p => p.name === 'production.batch_requests.manufacturing_orders.edit');
                      return permission ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          py: 1,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Edit
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Can edit manufacturing orders
                            </Typography>
                          </Box>
                          <Switch
                            checked={roleForm.permissions[permission.id]?.write || false}
                            onChange={(e) => {
                              const newPermissions = { ...roleForm.permissions };
                              if (!newPermissions[permission.id]) {
                                newPermissions[permission.id] = {};
                              }
                              newPermissions[permission.id].write = e.target.checked;
                              setRoleForm({ ...roleForm, permissions: newPermissions });
                            }}
                            size="small"
                          />
                        </Box>
                      ) : null;
                    })()}

                    {/* Manufacturing Orders Delete */}
                    {(() => {
                      const permission = permissions.find(p => p.name === 'production.batch_requests.manufacturing_orders.delete');
                      return permission ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          py: 1,
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Delete
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Can delete manufacturing orders
                            </Typography>
                          </Box>
                          <Switch
                            checked={roleForm.permissions[permission.id]?.delete || false}
                            onChange={(e) => {
                              const newPermissions = { ...roleForm.permissions };
                              if (!newPermissions[permission.id]) {
                                newPermissions[permission.id] = {};
                              }
                              newPermissions[permission.id].delete = e.target.checked;
                              setRoleForm({ ...roleForm, permissions: newPermissions });
                            }}
                            size="small"
                          />
                        </Box>
                      ) : null;
                    })()}

                    {/* Manufacturing Orders Approval */}
                    {(() => {
                      const permission = permissions.find(p => p.name === 'production.batch_requests.manufacturing_orders.approval');
                      return permission ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          py: 1
                        }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Approve Requests
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Can approve manufacturing order requests
                            </Typography>
                          </Box>
                          <Switch
                            checked={roleForm.permissions[permission.id]?.write || false}
                            onChange={(e) => {
                              const newPermissions = { ...roleForm.permissions };
                              if (!newPermissions[permission.id]) {
                                newPermissions[permission.id] = {};
                              }
                              newPermissions[permission.id].write = e.target.checked;
                              setRoleForm({ ...roleForm, permissions: newPermissions });
                            }}
                            size="small"
                          />
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {manufacturingPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderManufacturingPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default ProductionPlanningPermissions;
